import { useState } from 'react';

import { DataGrid, GridColDef, GridPaginationModel } from '@mui/x-data-grid';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { useNavigate } from 'react-router';

import { listCourseSectionsOptions } from '@drivingdoc/api-client/generated/react-query';
import { GetSectionsItem } from 'types/sections';

import { PageContainer } from 'components/PageContainer';

dayjs.extend(utc);
dayjs.extend(timezone);

const columns: GridColDef<GetSectionsItem>[] = [
  {
    field: 'title',
    type: 'string',
    headerName: 'Title',
    minWidth: 300,
  },
  {
    field: 'total_enrollment_count',
    type: 'number',
    headerName: 'Total Enrolled',
    minWidth: 150,
    valueGetter: (_, row) => Number(row.total_enrollment_count),
  },
  {
    field: 'capacity',
    type: 'number',
    headerName: 'Capacity',
    minWidth: 150,
    valueFormatter: (_, row) => (row.capacity === -1 ? 'No Limit' : row.capacity),
  },
  {
    field: 'open_enrollment',
    type: 'dateTime',
    headerName: 'Open Enrollment',
    minWidth: 250,
    valueGetter: (_, row) => (row.open_enrollment ? new Date(row.open_enrollment) : null),
    valueFormatter: (_, row) =>
      row.open_enrollment ? dayjs(row.open_enrollment).format('MMM Do YYYY, h:mm a z') : null,
  },
  {
    field: 'close_enrollment',
    type: 'dateTime',
    headerName: 'Close Enrollment',
    minWidth: 250,
    valueGetter: (_, row) => (row.close_enrollment ? new Date(row.close_enrollment) : null),
    valueFormatter: (_, row) =>
      row.close_enrollment ? dayjs(row.close_enrollment).format('MMM Do YYYY, h:mm a z') : null,
  },
  {
    field: 'tenant_location',
    type: 'string',
    headerName: 'Location',
    minWidth: 250,
    valueGetter: (_, row) => row.tenant_location?.name,
  },
];

export function SectionList() {
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    pageSize: 25,
    page: 0,
  });

  const navigate = useNavigate();

  // fetch sections - query will refetch when paginationModel changes
  const sections = useQuery({
    ...listCourseSectionsOptions({
      query: {
        page: paginationModel.page + 1,
        pageSize: paginationModel.pageSize,
      },
    }),
    // use previous data as placeholder to maintain a stable
    // reference while fetching the next page so the datagrid
    // doesn't reset the pagination model
    placeholderData: keepPreviousData,
  });
  const sectionData = sections.data as { data: GetSectionsItem[]; total: number } | undefined;

  return (
    <PageContainer title="Manage Sections">
      <DataGrid
        columns={columns}
        rows={sectionData?.data}
        loading={sections.isFetching}
        onRowClick={(params) => navigate(`/admin/sections/${params.row.id}`)}
        // enable serverside pagination
        paginationMode="server"
        rowCount={sectionData?.total ?? 0}
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        disableColumnFilter
        disableColumnSorting
      />
    </PageContainer>
  );
}

export default SectionList;
